import React from 'react';
import styled, { css } from 'styled-components';

import { Box, Flex, Paragraph, Text } from './UIKit/system';
import Container from './container';
import Subtitle from './subtitle';
import Title from './title';
import LazyImage from './lazy-image';
import { supportsGrid } from '../utils/styles';
import ArrowInCircle from './arrow-in-circle';
import mq from '../utils/mq';
import Link from './link';
import RichTextField from './rich-text-field';
import Button from './button';

const StyledIntro4 = styled(Box)`
  position: relative;
`;

const Links = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: -24px;
  max-width: 934px;
  margin-left: auto;
  margin-right: auto;

  > * {
    margin-right: 24px;
    margin-bottom: 24px;
    flex: 1;

    &:nth-of-type(4n) {
      margin-right: 0;
    }
  }

  ${supportsGrid(css`
    display: grid;
    grid-gap: 24px;
    margin-bottom: 0;
    grid-template-columns: repeat(4, 1fr);

    ${mq.tablet(css`
      grid-template-columns: repeat(2, 1fr);
      justify-content: center;
      align-items: center;
      > * :nth-of-type(2n + 1) {
        justify-self: end;
      }
    `)}

    ${mq.mobileSmall(css`
      grid-template-columns: repeat(1, 1fr);
      > * :nth-of-type(2n) {
        justify-self: center;
      }
      > * :nth-of-type(2n + 1) {
        justify-self: center;
      }
    `)}

    ${(props) =>
      props.centerItems &&
      css`
        grid-template-columns: repeat(3, 1fr);

        > * {
          height: auto;
        }

        ${mq.tablet(css`
          grid-auto-flow: row;

          > ${LinkItem} {
            height: auto;
            width: 100%;
          }
        `)}

        ${mq.mobile(css`
          grid-auto-flow: row;
          grid-template-columns: repeat(1, 1fr);
        `)}
      `}

    > * {
      margin-right: 0;
      margin-bottom: 0;
    }
  `)}
`;

const LinkItem = styled(Flex)`
  border-radius: var(--Radius-Radius-3, 16px);
  ${mq.tablet(css`
    width: 215px;
    height: 284px;
  `)};
  ${mq.mobile(css`
    width: 161px;
    height: 218px;
  `)};
`;

function Intro4(props) {
  const {
    title,
    titleProps = {},
    subtitle,
    text,
    links,
    bottomText,
    backgroundImage,
    backgroundColor = null,
    centerItems
  } = props;

  return (
    <StyledIntro4 pt={68} pb={[58, 80]} backgroundColor={backgroundColor}>
      {backgroundImage && <LazyImage asBackground fluid={backgroundImage} />}
      <Container position={'relative'}>
        <Flex
          flexDirection={'column'}
          alignItems={'center'}
          mb={45}
          textAlign={'center'}
        >
          {subtitle && <Subtitle mb={5}>{subtitle}</Subtitle>}
          <Title mb={15} as={'h1'} html={title} {...titleProps} />
          {text && (
            <Paragraph
              maxWidth={[500, 500, 900]}
              fontSize={15}
              grey
              html={text}
            />
          )}
        </Flex>
        <Links centerItems={centerItems}>
          {links.map((item, index) => {
            return (
              <LinkItem
                key={item.id || index}
                flexDirection={'column'}
                alignItems={'center'}
                justifyContent={'space-between'}
                height={284}
                pt={[24, 42, 42]}
                pb={24}
                px={15}
                bg={'white'}
                borderRadius={3}
              >
                <Link to={item.link} hover>
                  <Flex flexDirection={'column'} alignItems={'center'}>
                    <LazyImage
                      mb={[15, 30, 30]}
                      fluid={item.icon}
                      width={[77, 86]}
                    />
                    <Text fontSize={[13, 18, 20]} bold>
                      {item.name}
                    </Text>
                    {item.text && (
                      <Text
                        fontSize={[13, 16, 16]}
                        grey
                        m={[20, 30]}
                        textAlign="center"
                      >
                        {typeof item.text === 'string' ? (
                          item.text
                        ) : (
                          <RichTextField data={item.text} />
                        )}
                      </Text>
                    )}
                  </Flex>
                </Link>
                <Button
                  to={item.link}
                  iconLeft="arrowInCircle"
                  whiteTransparent
                >
                  {item.linkText}
                </Button>
              </LinkItem>
            );
          })}
        </Links>
        {bottomText && (
          <Paragraph
            maxWidth={900}
            ml={'auto'}
            mr={'auto'}
            textAlign={'center'}
            mt={54}
            greyAlt
            fontSize={15}
            html={bottomText}
          />
        )}
      </Container>
    </StyledIntro4>
  );
}

export default Intro4;
